import {
  ENVELOPE_SOURCE,
  ENVELOPE_VERSION,
  PAGE_CONTEXT_ENABLED_EVENT,
  WIDGET_BASE_URL,
  WIDGET_READY_EVENT,
} from "./constants"
import type { VinciWidgetEventName, VinciWidgetEventPayloads } from "./types"

type Handler<E extends VinciWidgetEventName> = (
  payload: VinciWidgetEventPayloads[E],
) => void

export interface ParentBridge {
  on<E extends VinciWidgetEventName>(
    event: E,
    handler: Handler<E>,
  ): () => void
  dispose(): void
}

export interface ParentBridgeCallbacks {
  onWidgetReady?: () => void
  onPageContextEnabled?: () => void
}

export function createParentBridge(
  iframe: HTMLIFrameElement,
  callbacks: ParentBridgeCallbacks = {},
): ParentBridge {
  const handlers = new Map<
    VinciWidgetEventName,
    Set<Handler<VinciWidgetEventName>>
  >()

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== WIDGET_BASE_URL) return
    if (event.source !== iframe.contentWindow) return
    if (event.data?.source !== ENVELOPE_SOURCE) return
    if (event.data?.version !== ENVELOPE_VERSION) return

    // Internal handshake signal — not part of the public event surface.
    if (event.data.event === WIDGET_READY_EVENT) {
      callbacks.onWidgetReady?.()
      return
    }
    if (event.data.event === PAGE_CONTEXT_ENABLED_EVENT) {
      callbacks.onPageContextEnabled?.()
      return
    }

    const name = event.data.event as VinciWidgetEventName
    const payload = event.data.payload ?? {}
    const set = handlers.get(name)
    if (set) {
      for (const h of set) {
        try {
          h(payload)
        } catch (err) {
          console.error("[VinciWidgetSDK] handler threw", err)
        }
      }
    }
  }

  window.addEventListener("message", onMessage)

  return {
    on(event, handler) {
      if (!handlers.has(event)) handlers.set(event, new Set())
      const set = handlers.get(event)!
      set.add(handler as Handler<VinciWidgetEventName>)
      return () => {
        set.delete(handler as Handler<VinciWidgetEventName>)
      }
    },
    dispose: () => {
      window.removeEventListener("message", onMessage)
      handlers.clear()
    },
  }
}
